import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiStar,
  FiRefreshCw,
  FiCheck,
  FiClock,
  FiMail,
  FiUser,
  FiCopy,
  FiInfo,
} from 'react-icons/fi';
import AdminLayout from '../components/AdminLayout';
import {
  AdminPageHeader,
  AdminKpiGrid,
  AdminEmpty,
  AdminSection,
  pillClass,
  formatArs,
  formatDate,
} from '../components/admin';
import { FormAlert, formInputClass } from '../components/form';
import { useApi } from '../hooks/useApi';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useThemeClasses } from '../hooks/useThemeClasses';

const PHASE_LABELS = {
  active: 'En beta',
  ending_soon: 'Por vencer',
  ended: 'Beta vencida',
  converted: 'Suscrito (MP)',
  unknown: 'Sin datos',
};

const PHASE_PILL = {
  active: 'success',
  ending_soon: 'warning',
  ended: 'danger',
  converted: 'accent',
  unknown: 'neutral',
};

const FILTER_CHIPS = [
  { id: 'all', label: 'Todos' },
  { id: 'attention', label: 'Atención' },
  { id: 'ending', label: 'Por vencer' },
  { id: 'active', label: 'En curso' },
  { id: 'ended', label: 'Vencidos' },
  { id: 'converted', label: 'Suscritos' },
];

function formatSentAt(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function daysLabel(row) {
  if (row.daysRemaining == null) return 'Sin fecha de fin';
  if (row.daysRemaining >= 0) {
    return `${row.daysRemaining} día${row.daysRemaining === 1 ? '' : 's'} restantes`;
  }
  return `Venció hace ${Math.abs(row.daysRemaining)} día${Math.abs(row.daysRemaining) === 1 ? '' : 's'}`;
}

function MilestoneTimeline({ milestones, modoOscuro }) {
  if (!milestones?.length) {
    return <p className="text-xs text-gray-500">Sin datos de avisos</p>;
  }
  return (
    <ul className="grid grid-cols-5 gap-1.5">
      {milestones.map((m) => {
        const sent = m.status === 'sent';
        const due = m.status === 'due';
        const missed = m.status === 'missed';
        const tone = sent ? 'success' : due ? 'warning' : missed ? 'danger' : 'neutral';
        return (
          <li
            key={m.key}
            title={
              sent
                ? `Enviado ${formatSentAt(m.sentAt) || ''}`
                : due
                  ? 'Pendiente de envío (job diario)'
                  : missed
                    ? 'No registrado (venció antes del aviso)'
                    : 'Aún no corresponde'
            }
            className={`rounded-lg border px-1.5 py-2 text-center ${pillClass(modoOscuro, tone)}`}
          >
            <p className="text-[10px] font-semibold tabular-nums">{m.days}d</p>
            <p className="text-[9px] mt-0.5 opacity-90">
              {sent ? 'Enviado' : due ? 'Pendiente' : missed ? 'Faltó' : '—'}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

function BetaTesterCard({ row, modoOscuro, planMonthlyPriceArs, onCopyEmail }) {
  const tc = useThemeClasses();
  const urgent = row.needsAttention || row.phase === 'ending_soon';
  const borderClass = urgent
    ? modoOscuro
      ? 'border-amber-700/60 ring-1 ring-amber-800/40'
      : 'border-amber-300 ring-1 ring-amber-100'
    : modoOscuro
      ? 'border-gray-800'
      : 'border-gray-200';

  return (
    <article className={`rounded-2xl border p-4 ${borderClass} ${tc.modoOscuro ? 'bg-gray-900/30' : 'bg-white'}`}>
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold ${
            modoOscuro ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
          }`}
        >
          {row.name?.charAt(0)?.toUpperCase() || <FiUser className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={`text-sm font-semibold truncate ${tc.textPri}`}>{row.name}</p>
              <div className="flex items-center gap-1 min-w-0">
                <p className={`text-xs truncate ${tc.textMuted}`}>{row.email}</p>
                <button
                  type="button"
                  onClick={() => onCopyEmail(row.email)}
                  className={`shrink-0 p-1 rounded-md ${tc.textMuted} hover:text-[#1F80FF]`}
                  title="Copiar email"
                >
                  <FiCopy className="w-3 h-3" />
                </button>
              </div>
            </div>
            <span className={pillClass(modoOscuro, PHASE_PILL[row.phase] || 'neutral')}>
              {PHASE_LABELS[row.phase] || row.phase}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
            <div>
              <p
                className={`text-lg font-bold tabular-nums ${
                  row.daysRemaining != null && row.daysRemaining <= 7 && !row.converted
                    ? 'text-amber-600 dark:text-amber-300'
                    : tc.textPri
                }`}
              >
                {row.converted ? 'Plan pago activo' : daysLabel(row)}
              </p>
              <p className={`text-[11px] ${tc.textMuted}`}>
                Vence {formatDate(row.betaProEndsAt)} · Alta {formatDate(row.grantedAt)}
              </p>
            </div>
            {!row.converted && row.percentUsed != null && (
              <span className={`text-xs font-medium tabular-nums ${tc.textSec}`}>{row.percentUsed}% usado</span>
            )}
          </div>

          {!row.converted && (
            <div className={`mt-2 h-2 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div
                className={`h-full transition-all ${
                  row.daysRemaining != null && row.daysRemaining <= 14 ? 'bg-amber-500' : 'bg-[#1F80FF]'
                }`}
                style={{ width: `${row.percentUsed ?? 0}%` }}
              />
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <div className={`rounded-lg px-2 py-1.5 ${tc.section}`}>
              <p className={tc.textMuted}>Suscripción</p>
              <p className={`font-medium ${tc.textPri}`}>
                {planMonthlyPriceArs != null ? `${formatArs(planMonthlyPriceArs)}/mes` : 'Profesional'}
              </p>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${tc.section}`}>
              <p className={tc.textMuted}>Uso</p>
              <p className={`font-medium ${tc.textPri}`}>
                {row.daysElapsed != null ? `${row.daysElapsed} / ${row.totalDays} d` : '—'}
              </p>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${tc.section}`}>
              <p className={tc.textMuted}>Cuenta</p>
              <p className={`font-medium capitalize ${tc.textPri}`}>{row.subscriptionStatus || '—'}</p>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${tc.section}`}>
              <p className={tc.textMuted}>Pago</p>
              <p className={`font-medium ${tc.textPri}`}>
                {row.converted ? 'Mercado Pago' : row.paymentProvider === 'beta_comp' ? 'Beta (sin MP)' : '—'}
              </p>
            </div>
          </div>

          {!row.converted && (
            <div className="mt-4">
              <p className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${tc.textMuted}`}>
                Avisos por email (30 · 14 · 7 · 3 · 1 día)
              </p>
              <MilestoneTimeline milestones={row.notifications?.milestones} modoOscuro={modoOscuro} />
              <div className={`mt-2 space-y-0.5 text-[10px] ${tc.textSec}`}>
                {row.notifications?.endedEmailAt ? (
                  <p>Fin de beta: {formatSentAt(row.notifications.endedEmailAt)}</p>
                ) : row.phase === 'ended' ? (
                  <p className="text-amber-700 dark:text-amber-300">Fin de beta: email no registrado</p>
                ) : null}
                {row.notifications?.graceStartedEmailAt && (
                  <p>Solo lectura (gracia): {formatSentAt(row.notifications.graceStartedEmailAt)}</p>
                )}
                {row.missingCriticalReminder && (
                  <p className="text-amber-700 dark:text-amber-300 font-medium">Falta aviso de 7 días — revisá SMTP / job diario</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

const AdminBetaProgram = () => {
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const { canManageSystem } = useAdminPermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copyMsg, setCopyMsg] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('urgency');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await secureFetch('/api/admin/beta-program');
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'No se pudo cargar el programa beta');
      setData(json);
    } catch (err) {
      setError(err.message || 'Error al cargar');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const users = useMemo(() => data?.users ?? [], [data]);
  const summary = useMemo(() => data?.summary ?? {}, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.filter((row) => {
      if (filter === 'attention' && !row.needsAttention) return false;
      if (filter === 'ending' && row.phase !== 'ending_soon') return false;
      if (filter === 'ended' && row.phase !== 'ended') return false;
      if (filter === 'converted' && row.phase !== 'converted') return false;
      if (filter === 'active' && row.phase !== 'active') return false;
      if (!q) return true;
      return (
        String(row.name || '').toLowerCase().includes(q)
        || String(row.email || '').toLowerCase().includes(q)
      );
    });

    list = [...list];
    if (sort === 'name') {
      list.sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
    } else if (sort === 'granted') {
      list.sort((a, b) => new Date(b.grantedAt || 0) - new Date(a.grantedAt || 0));
    } else {
      list.sort((a, b) => {
        if (Boolean(a.needsAttention) !== Boolean(b.needsAttention)) {
          return a.needsAttention ? -1 : 1;
        }
        const da = a.daysRemaining ?? 9999;
        const db = b.daysRemaining ?? 9999;
        return da - db;
      });
    }
    return list;
  }, [users, filter, search, sort]);

  const cupoPct = data?.limit > 0 ? Math.min(100, Math.round(((data.granted || 0) / data.limit) * 100)) : 0;
  const conversionPct =
    users.length > 0 ? Math.round(((summary.converted || 0) / users.length) * 100) : 0;

  const kpis = data
    ? [
        {
          icon: FiStar,
          label: 'Cupos usados',
          value: `${data.granted ?? 0} / ${data.limit ?? 0}`,
          hint: data.enabled ? `${data.remaining ?? 0} disponibles` : 'Registro deshabilitado',
          onClick: () => setFilter('all'),
        },
        {
          icon: FiClock,
          label: 'Por vencer (14d)',
          value: summary.endingSoon ?? 0,
          hint: 'Beneficio beta en curso',
          onClick: () => setFilter('ending'),
        },
        {
          icon: FiMail,
          label: 'Sin aviso 7d',
          value: summary.missingReminder7d ?? 0,
          hint: 'Revisar correo / jobs',
          onClick: () => setFilter('attention'),
        },
        {
          icon: FiCheck,
          label: 'Conversión a pago',
          value: `${summary.converted ?? 0} (${conversionPct}%)`,
          hint: 'Mercado Pago activo',
          onClick: () => setFilter('converted'),
        },
      ]
    : [];

  const handleCopyEmail = async (email) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopyMsg('Email copiado');
      window.setTimeout(() => setCopyMsg(''), 2000);
    } catch {
      setCopyMsg('No se pudo copiar');
    }
  };

  const chipCount = (id) => {
    if (id === 'all') return users.length;
    if (id === 'attention') return summary.needsAttention ?? users.filter((u) => u.needsAttention).length;
    if (id === 'ending') return summary.endingSoon ?? users.filter((u) => u.phase === 'ending_soon').length;
    if (id === 'active') return summary.active ?? users.filter((u) => u.phase === 'active').length;
    if (id === 'ended') return summary.ended ?? users.filter((u) => u.phase === 'ended').length;
    if (id === 'converted') return summary.converted ?? users.filter((u) => u.phase === 'converted').length;
    return 0;
  };

  return (
    <AdminLayout requiredPermission={canManageSystem}>
      <div className="max-w-7xl mx-auto py-6 space-y-5 min-w-0 w-full">
        <AdminPageHeader
          title="Programa beta Pro"
          subtitle="Cupos de lanzamiento, plazos de uso gratuito, avisos automáticos y paso a suscripción paga."
          badge={
            data?.planMonthlyPriceArs != null ? (
              <span className={pillClass(tc.modoOscuro, 'neutral')}>
                Luego {formatArs(data.planMonthlyPriceArs)}/mes
              </span>
            ) : null
          }
          actions={
            <>
              <Link
                to="/admin/dashboard"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tc.btnOutline}`}
              >
                Inicio admin
              </Link>
              <button
                type="button"
                onClick={load}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tc.btnOutline}`}
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </>
          }
        />

        {error && <FormAlert type="error">{error}</FormAlert>}
        {copyMsg && <FormAlert type="success">{copyMsg}</FormAlert>}

        {loading && !data ? (
          <div className={`rounded-2xl border p-10 text-center ${tc.box}`}>
            <div className="w-9 h-9 border-[3px] border-gray-300 border-t-[#1F80FF] rounded-full animate-spin mx-auto mb-3" />
            <p className={`text-sm ${tc.textSec}`}>Cargando programa beta…</p>
          </div>
        ) : data ? (
          <>
            {(summary.needsAttention ?? 0) > 0 && (
              <FormAlert type="warning" className="text-sm">
                <strong>{summary.needsAttention}</strong> tester(s) requieren revisión (por vencer sin aviso o beta vencida sin
                pago).
              </FormAlert>
            )}

            <AdminKpiGrid items={kpis} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <AdminSection
                className="lg:col-span-2"
                title="Cupo del programa"
                description={`Plan ${data.planName || 'Profesional'} · ${data.freeDays || 120} días gratis al registrarse`}
                icon={FiStar}
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={pillClass(tc.modoOscuro, data.enabled ? 'success' : 'neutral')}>
                    {data.enabled ? 'Registro beta activo' : 'Registro deshabilitado'}
                  </span>
                  <span className={pillClass(tc.modoOscuro, 'neutral')}>
                    {data.remaining ?? 0} cupos libres
                  </span>
                </div>
                {data.enabled && data.limit > 0 && (
                  <>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className={tc.textSec}>{data.granted ?? 0} asignados</span>
                      <span className={tc.textMuted}>{cupoPct}% del cupo</span>
                    </div>
                    <div className={`h-2.5 rounded-full overflow-hidden ${tc.modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="h-full bg-[#1F80FF] transition-all" style={{ width: `${cupoPct}%` }} />
                    </div>
                  </>
                )}
                <p className={`text-[11px] mt-3 ${tc.textMuted}`}>
                  Origen de alta: <code>{data.paymentSource}</code>. Variables: BETA_PRO_SIGNUP_ENABLED, BETA_PRO_FREE_SLOTS,
                  BETA_PRO_FREE_DAYS.
                </p>
              </AdminSection>

              <AdminSection
                title="Cómo funcionan los avisos"
                icon={FiInfo}
              >
                <ul className={`text-xs space-y-2 ${tc.textSec}`}>
                  <li>Emails automáticos a <strong>30, 14, 7, 3 y 1</strong> día antes del vencimiento.</li>
                  <li>Al terminar el período sin Mercado Pago: <strong>solo lectura</strong> + mail de activación de plan.</li>
                  <li>Los envíos corren con el <strong>job diario</strong> de suscripciones en el backend.</li>
                </ul>
              </AdminSection>
            </div>

            <AdminSection
              title="Testers"
              description={`${filtered.length} de ${users.length} mostrados`}
              icon={FiUser}
            >
              <div className="flex flex-col lg:flex-row gap-2.5 mb-4">
                <div className="relative flex-1 min-w-0">
                  <input
                    type="search"
                    placeholder="Buscar por nombre o email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`${formInputClass(tc.inputClass, { compact: true })} w-full`}
                  />
                </div>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className={formInputClass(tc.inputClass, { compact: true }) + ' lg:w-44'}
                >
                  <option value="urgency">Orden: más urgente</option>
                  <option value="granted">Alta más reciente</option>
                  <option value="name">Nombre A–Z</option>
                </select>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {FILTER_CHIPS.map((chip) => {
                  const active = filter === chip.id;
                  const count = chipCount(chip.id);
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      onClick={() => setFilter(chip.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                        active
                          ? 'bg-[#1F80FF] text-white border-[#1F80FF]'
                          : pillClass(tc.modoOscuro, 'neutral')
                      }`}
                    >
                      {chip.label}
                      <span className={`tabular-nums ${active ? 'text-white/90' : ''}`}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {filtered.length === 0 ? (
                <AdminEmpty
                  icon={FiStar}
                  title="Sin resultados"
                  subtitle="Probá otro filtro o limpiá la búsqueda."
                />
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  {filtered.map((row) => (
                    <BetaTesterCard
                      key={row.userId}
                      row={row}
                      modoOscuro={tc.modoOscuro}
                      planMonthlyPriceArs={data.planMonthlyPriceArs}
                      onCopyEmail={handleCopyEmail}
                    />
                  ))}
                </div>
              )}
            </AdminSection>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
};

export default AdminBetaProgram;
